import collect from 'collect.js';

/**
 * DeptServiceCls: a service class to provide support for organisation departments.
 */

class DeptServiceCls {
  /**
   * Flatten a dept hierarchy list as returned from backend.
   */
  flattenDeptHierarchy(deptHierarchy) {
    const flattened = [];

    const addItem = (item) => {
      flattened.push(item);
      for (const child of item.children) {
        addItem(child);
      }
    };

    for (const item of deptHierarchy || []) {
      addItem(item);
    }

    return flattened;
  }

  /**
   * Get the dept labels as a concatenated label.
   * e.g. "Department/Unit/Team"
   */
  deptLabelsAsConcatenatedLabel(deptLabels, plural = true, sep = ' / ') {
    return (deptLabels || [])
      .map((item) => (plural ? item.plural : item.label))
      .join(sep);
  }

  /**
   * Get the given dept ids as a concatenated label.
   * e.g. "Department/Unit"
   */
  deptsAsConcatenatedLabel(deptHierarchy, deptIds, sep = ' / ') {
    return (deptIds || [])
      .map((id) => deptHierarchy.find((dept) => dept.id === id)?.name || null)
      .filter((lbl) => !!lbl)
      .join(sep);
  }

  /**
   * Get dept label for the given "level". The first level is 1.
   */
  getDeptLabelForLevel(deptLabels, level, plural = false) {
    if (!level) {
      return '';
    }
    return deptLabels?.[level - 1]?.[plural ? 'plural' : 'label'] || '';
  }

  /**
   * Get dept label for the top level.
   */
  getDeptLabelForTopLevel(deptLabels, plural = false) {
    return this.getDeptLabelForLevel(deptLabels, 1, plural);
  }

  /**
   * See if the given level is the "youngest" child level.
   */
  isYoungestDeptLevel(deptLabels, level) {
    if (!deptLabels || !deptLabels.length) {
      return false;
    }

    return level >= deptLabels[deptLabels.length - 1].level;
  }

  /**
   * Get the dept record by its id.
   */
  getDeptById(deptId, deptHierarchy) {
    if (!deptHierarchy) {
      return null;
    }

    const checkDeptAndChildren = (checkDept) => {
      if (checkDept.id === deptId) {
        return checkDept;
      }
      for (const childDept of checkDept.children || []) {
        const resp = checkDeptAndChildren(childDept);
        if (resp) {
          return resp;
        }
      }
      return null;
    };

    for (const d of deptHierarchy) {
      const resp = checkDeptAndChildren(d);
      if (resp) {
        return resp;
      }
    }

    return null;
  }

  /**
   * Get the parent of the given dept. If level > 1, then the
   * parent is the immediate ancestor with level == level-1, o/w
   * parent is null.
   */
  getParentForDept(dept, deptHierarchy) {
    if (!deptHierarchy) {
      return null;
    }
    if (!dept) {
      return null;
    }

    const checkDeptAndChildren = (checkDept) => {
      if (checkDept.id === dept.parentDepartmentId) {
        return checkDept;
      }
      for (const childDept of checkDept.children || []) {
        const resp = checkDeptAndChildren(childDept);
        if (resp) {
          return resp;
        }
      }
      return null;
    };

    for (const d of deptHierarchy) {
      const resp = checkDeptAndChildren(d);
      if (resp) {
        return resp;
      }
    }

    return null;
  }

  /**
   * Order a list of departments as per the given hierarchy.
   */
  orderDeptsAsPerHierarchy(depts, deptHierarchyFlattened) {
    if (!deptHierarchyFlattened) {
      return [];
    }

    depts = collect(depts);
    const ordered = [];
    deptHierarchyFlattened.forEach((dept) => {
      const d = depts.firstWhere('id', dept.id);
      if (d) {
        ordered.push(d);
      }
    });

    return ordered;
  }
}

// service class is a singleton
const DeptService = new DeptServiceCls();

export { DeptService };
